import React, { useCallback, useEffect, useRef, useState } from "react";

// Kanban marquee (click-and-drag rectangle) selection wrapper.
// Only starts a marquee when mouseDown lands on an empty area (not on a
// card, checkbox, dropdown or button). On mouseUp, iterates every DOM
// node matching `cardSelector`, intersects its bounding rect with the
// drawn rectangle, and yields the project id (parsed from
// `data-testid="pm-card-<id>"`).
// Cancelling: Escape or an unmount mid-drag.

const MIN_DRAG_PX = 6; // ignore accidental micro-drags

function isInteractive(el) {
  if (!el) return true;
  // Ignore drags that started inside interactive DOM (checkboxes, buttons,
  // selects, links, cards themselves) — those own their own click / drag
  // handlers.
  return !!el.closest(
    "input,button,select,textarea,a,[role='button']," +
    "[data-testid^='pm-card-'][data-classification]," +
    "[data-testid^='pm-card-select-']," +
    "[data-testid^='pm-card-risk-']"
  );
}

export default function MarqueeSelector({ enabled, onSelect, cardSelector, children }) {
  const rootRef = useRef(null);
  const [rect, setRect] = useState(null); // { x, y, w, h } in page coords
  const startRef = useRef(null);           // { x, y, shiftKey }

  const finish = useCallback((commit) => {
    const r = rect;
    const wasShift = !!r?.shiftKey;
    startRef.current = null;
    setRect(null);
    if (!commit || !r) return;
    if (r.w < MIN_DRAG_PX && r.h < MIN_DRAG_PX) return; // treated as a click
    // Iterate cards & find intersections.
    const root = rootRef.current;
    if (!root) return;
    const cards = root.querySelectorAll(cardSelector);
    const ids = [];
    const box = { left: r.x, top: r.y, right: r.x + r.w, bottom: r.y + r.h };
    cards.forEach((c) => {
      const b = c.getBoundingClientRect();
      // getBoundingClientRect gives viewport coords → convert page coords
      // by adding window scroll.
      const cb = {
        left: b.left + window.scrollX,
        top: b.top + window.scrollY,
        right: b.right + window.scrollX,
        bottom: b.bottom + window.scrollY,
      };
      const intersects = !(cb.right < box.left || cb.left > box.right ||
                            cb.bottom < box.top || cb.top > box.bottom);
      if (!intersects) return;
      const tid = c.getAttribute("data-testid") || "";
      const m = tid.match(/^pm-card-([\w-]+)$/);
      if (m) ids.push(m[1]);
    });
    onSelect?.(ids, { shiftKey: wasShift });
  }, [rect, onSelect, cardSelector]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onMove = (e) => {
      const s = startRef.current;
      if (!s) return;
      const x = e.pageX;
      const y = e.pageY;
      setRect({
        x: Math.min(s.x, x),
        y: Math.min(s.y, y),
        w: Math.abs(x - s.x),
        h: Math.abs(y - s.y),
        shiftKey: s.shiftKey,
      });
    };
    const onUp = () => finish(true);
    const onKey = (e) => { if (e.key === "Escape") finish(false); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("keydown", onKey);
    };
  }, [enabled, finish]);

  const onMouseDown = (e) => {
    if (!enabled) return;
    if (e.button !== 0) return; // primary button only
    if (isInteractive(e.target)) return;
    startRef.current = { x: e.pageX, y: e.pageY, shiftKey: e.shiftKey };
    setRect({ x: e.pageX, y: e.pageY, w: 0, h: 0, shiftKey: e.shiftKey });
  };

  return (
    <div
      ref={rootRef}
      onMouseDown={onMouseDown}
      style={{ position: "relative", userSelect: rect ? "none" : "auto" }}
      data-testid="pm-marquee-root"
    >
      {children}
      {rect && (rect.w > 0 || rect.h > 0) && (
        <div
          data-testid="pm-marquee-rect"
          style={{
            position: "absolute",
            left: rect.x - (rootRef.current?.getBoundingClientRect().left + window.scrollX || 0),
            top: rect.y - (rootRef.current?.getBoundingClientRect().top + window.scrollY || 0),
            width: rect.w,
            height: rect.h,
            border: "1.5px dashed var(--accent-hover)",
            background: "var(--accent-hover)15",
            pointerEvents: "none",
            zIndex: 30,
          }}
        />
      )}
    </div>
  );
}
